import { useHandler } from "@redotech/react-util/hook";
import TrashIcon from "@redotech/redo-icons/trash-01.svg";
import { WeightChangeSource } from "@redotech/redo-merchant-app-common/components/weight-widget/total-weight-widget";
import type { CarrierAndService } from "@redotech/redo-model/fulfillments/fulfillment-carriers-and-services";
import {
  FulfillmentGroupStatus,
  isEditingLockedStatus,
} from "@redotech/redo-model/fulfillments/fulfillment-group-status";
import {
  FulfillmentOrderLineItem,
  groupFulfillmentOrderLineItems,
} from "@redotech/redo-model/fulfillments/fulfillment-order-line-item";
import { FulfillmentGroupActions } from "@redotech/redo-model/outbound-labels/fulfillment-group-actions";
import {
  cleanAllocations,
  computeParcelWeights,
  OutboundParcel,
  Parcel,
} from "@redotech/redo-model/outbound-labels/parcel";
import {
  convertWeight,
  WeightUnit,
} from "@redotech/redo-model/outbound-labels/util";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useEffect, useMemo, useState } from "react";
import type { DisplayParcel } from "../../utils/use-group-info";
import { ParcelInput } from "./parcel-input";
import { ParcelItemBreakdown } from "./parcel-item-breakdown";

interface ItemAllocation {
  externalId: string;
  sku: string;
  quantity: number;
}

export const ParcelModal = memo(function ParcelModal({
  displayedButtonAction,
  parcelValues,
  selectedParcels,
  onParcelChange,
  onRemoveParcel,
  onParcelAdd,
  open,
  onModalClose,
  selectedRate,
  selectedStatus,
  everythingDisabled,
  mixedParcels,
  usedPackageLearning,
  usedWeightLearning,
  trackingNumbers,
  items,
  onAllocationsFlush,
}: {
  displayedButtonAction: FulfillmentGroupActions;
  parcelValues: readonly Parcel[];
  selectedParcels: DisplayParcel[];
  onParcelChange: (
    parcel: Partial<OutboundParcel>,
    index: number,
    source?: WeightChangeSource,
  ) => void;
  onRemoveParcel: (index: number) => void;
  onParcelAdd: (parcel: Parcel) => void;
  open: boolean;
  onModalClose: () => void;
  selectedRate?: CarrierAndService | null;
  selectedStatus: FulfillmentGroupStatus | null | undefined;
  everythingDisabled: boolean;
  mixedParcels: boolean;
  usedPackageLearning: boolean;
  usedWeightLearning: boolean;
  trackingNumbers: string[] | undefined;
  items: FulfillmentOrderLineItem[];
  onAllocationsFlush?: (
    allocations: Map<
      number,
      { externalId: string; sku: string; quantity: number }[]
    >,
  ) => void;
}) {
  const dedupedItems = useMemo(
    () => groupFulfillmentOrderLineItems(items),
    [items],
  );

  // Draft allocations — only the allocation edits the user makes in the modal.
  // Keyed by parcel index. Absent keys fall through to selectedParcels[i].
  const [draftAllocations, setDraftAllocations] = useState<
    Map<number, ItemAllocation[]>
  >(new Map());

  // Snapshot current allocations when modal opens
  useEffect(() => {
    if (open) {
      const snapshot = new Map<number, ItemAllocation[]>();
      for (const [i, parcel] of selectedParcels.entries()) {
        snapshot.set(
          i,
          (parcel.itemAllocations ?? []).map((a) => ({ ...a })),
        );
      }
      setDraftAllocations(snapshot);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Overlay draft allocations onto real parcels, then clean + compute weights
  // (but do NOT auto-assign unallocated items — that's the whole point)
  const displayParcels: DisplayParcel[] = useMemo(() => {
    const withDraftAllocations = selectedParcels.map((parcel, i) => ({
      ...parcel,
      itemAllocations: draftAllocations.get(i) ?? parcel.itemAllocations ?? [],
      parcelAndContentWeight: parcel.hasWeightOverride
        ? parcel.parcelAndContentWeight
        : null,
    }));
    const cleaned = cleanAllocations(withDraftAllocations, dedupedItems);
    const weighted = computeParcelWeights(cleaned, dedupedItems);
    return weighted.map((parcel, i) => ({
      ...parcel,
      hasWeightOverride: selectedParcels[i]?.hasWeightOverride ?? false,
    }));
  }, [selectedParcels, draftAllocations, dedupedItems]);

  const onDraftAllocationChange = useHandler(
    (change: Partial<OutboundParcel>, index: number) => {
      if (change.itemAllocations) {
        setDraftAllocations((prev) => {
          const next = new Map(prev);
          next.set(index, change.itemAllocations!);
          return next;
        });
      }
    },
  );

  const getOnParcelChange = useHandler((index: number) => {
    return (parcel: Partial<OutboundParcel>, source?: WeightChangeSource) => {
      onParcelChange(parcel, index, source);
    };
  });

  const onDuplicateParcel = useHandler((index: number) => {
    if (!selectedParcels[index]) {
      return;
    }
    const { itemAllocations: _, ...parcelWithoutAllocations } =
      selectedParcels[index];
    onParcelAdd(parcelWithoutAllocations);
  });

  const onAddPackage = useHandler(() => {
    if (!selectedParcels || selectedParcels.length === 0) {
      return;
    }

    const firstParcel = selectedParcels[0];

    if (!firstParcel) {
      return;
    }

    const newParcel: OutboundParcel = {
      ...firstParcel,
      customs: null,
      hazmat: null,
      dryIceWeight: null,
      parcelAndContentWeight: undefined,
      insurance: undefined,
      itemAllocations: [],
    };

    onParcelAdd(newParcel);
  });

  const handleModalClose = useHandler(() => {
    if (onAllocationsFlush) {
      onAllocationsFlush(draftAllocations);
    }
    onModalClose();
  });

  const hasUnallocatedItems = useMemo(() => {
    if (!dedupedItems?.length) {
      return false;
    }
    for (const item of dedupedItems) {
      let allocated = 0;
      for (const parcel of displayParcels) {
        for (const alloc of parcel.itemAllocations ?? []) {
          if (alloc.externalId === item.externalId) {
            allocated += alloc.quantity;
          }
        }
      }
      if (allocated < item.quantity) {
        return true;
      }
    }
    return false;
  }, [dedupedItems, displayParcels]);

  const totalWeightInOunces = useMemo(() => {
    return displayParcels.reduce((acc, parcel) => {
      const weight = parcel.parcelAndContentWeight ?? {
        value: 0,
        unit: WeightUnit.GRAM,
      };
      return acc + convertWeight(weight, WeightUnit.OUNCE).value;
    }, 0);
  }, [displayParcels]);

  return (
    <RedoModal
      contentScrollable
      isOpen={open}
      onModalCloseRequested={handleModalClose}
      primaryButton={{ text: "Done", onClick: handleModalClose }}
      title="Fulfillment packages"
    >
      <Flex dir="column" gap="lg">
        <Flex align="center" dir="row" justify="space-between">
          <Flex dir="column" gap="xs">
            <Text fontSize="md" fontWeight="medium">
              {selectedParcels.length} package
              {selectedParcels.length === 1 ? "" : "s"}
            </Text>
            <Text color="tertiary" fontSize="xs" fontWeight="regular">
              {Math.floor(totalWeightInOunces / 16)} lb{" "}
              {totalWeightInOunces % 16} oz
            </Text>
          </Flex>
          <RedoButton
            disabled={isEditingLockedStatus(selectedStatus)}
            hierarchy="primary"
            onClick={onAddPackage}
            size="sm"
            text="Add package"
          />
        </Flex>
        {hasUnallocatedItems && (
          <Text fontSize="xs" fontWeight="medium" textColor="error">
            Some items are not assigned to a package. Unassigned items will be
            automatically assigned to Package 1 on save.
          </Text>
        )}
        <Flex dir="column" gap="lg">
          {displayParcels.map((parcel, index) => (
            <Flex
              bgColor="secondary"
              borderColor="primary"
              borderStyle="solid"
              borderWidth="1px"
              dir="column"
              key={`${parcel._id}-${index}`}
              p="2xl"
              radius="md"
            >
              <Flex align="center" dir="row" justify="space-between">
                <Text>Package {index + 1}</Text>
                <Flex align="center" gap="xs">
                  <RedoButton
                    disabled={isEditingLockedStatus(selectedStatus)}
                    hierarchy="tertiary"
                    onClick={() => onDuplicateParcel(index)}
                    size="sm"
                    text="Duplicate"
                  />
                  <RedoButton
                    disabled={
                      selectedParcels.length === 1 ||
                      isEditingLockedStatus(selectedStatus)
                    }
                    hierarchy="tertiary"
                    IconLeading={TrashIcon}
                    onClick={() => onRemoveParcel(index)}
                    size="sm"
                    theme="destructive"
                  />
                </Flex>
              </Flex>
              {trackingNumbers?.[index] && (
                <Text color="tertiary" fontSize="xs" fontWeight="regular">
                  {`Tracking number: ${trackingNumbers[index]}`}
                </Text>
              )}
              <ParcelInput
                disableAutoReadFromScale
                displayedButtonAction={displayedButtonAction}
                everythingDisabled={everythingDisabled}
                mixedParcels={mixedParcels}
                mixedWeight={false}
                onParcelChange={getOnParcelChange(index)}
                parcelValues={parcelValues}
                selectedParcel={parcel}
                selectedRate={selectedRate}
                selectedStatus={selectedStatus}
                usedPackageLearning={usedPackageLearning}
                usedWeightLearning={usedWeightLearning}
              />
              {dedupedItems?.length > 0 && (
                <ParcelItemBreakdown
                  disabled={
                    everythingDisabled || isEditingLockedStatus(selectedStatus)
                  }
                  items={dedupedItems}
                  onParcelChange={onDraftAllocationChange}
                  parcelIndex={index}
                  selectedParcels={displayParcels}
                />
              )}
            </Flex>
          ))}
        </Flex>
      </Flex>
    </RedoModal>
  );
});
